import { of, Observable } from 'rxjs';

import { Injectable, inject } from '@angular/core';
import { ProductsApiService } from './products-api.service';
import { tap } from 'rxjs/operators';
import { UserService } from 'src/app/core/services/user/user.service';
import { UserSession } from 'src/app/core/models/user.model';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { StorageService } from 'src/app/core/services/storage/storage.service';

export const DEFAULT_DESTINATION_PINCODE = '400001';

export interface ExpectedDeliveryDates {
  estimated_days_for_delivery?: EstimatedDaysForDelivery
  message: string
  code?: number
}

export interface EstimatedDaysForDelivery {
  minimum_days: number
  maximum_days: number
  average_days: number
  fastest_delivery_by: string
  slowest_delivery_by: string
}

export interface ExpectedDeliveryStoredResponse {
  pincode: string;
  createdAt: Date;
  pincodeResponse: ExpectedDeliveryDates
}

export interface ExpectedDeliveryDatesCache {
  [key: string]: ExpectedDeliveryStoredResponse[]
}

@Injectable({
  providedIn: 'root'
})

export class ExpectedDeliveryService {
  private productsApiService = inject(ProductsApiService);
  private userService = inject(UserService);
  private platformService = inject(PlatformService);
  private storageService = inject(StorageService);

  user: UserSession = new UserSession();
  expectedDeliveryDatesCache: ExpectedDeliveryDatesCache = {}

  getExpectedDeliveryDates(productId: string, pincode: string): Observable<ExpectedDeliveryDates> {
    /* Retrieving saved estimated delivery from local storage */
    let cachedEstimatedDeliverDate = null;
    if (this.platformService.isBrowser()) {
      try {
        cachedEstimatedDeliverDate = this.storageService.retrieve("estimatedDeliveryDate");
      } catch (error) {
        cachedEstimatedDeliverDate = null;
      }
    }

    let expectedDeliveryCacheHit: ExpectedDeliveryStoredResponse | null = null;
    const appUser = this.userService.appUser;
    if (appUser) {
      this.user = appUser;
    }

    /* For empty pincode call  userPincode set 400001 */
    const userPincode = pincode ? pincode : DEFAULT_DESTINATION_PINCODE;

    if (this.user) {
      try {
        /* Checking estimated delivery response cached for given product id and pincode */
        const cachedArray = cachedEstimatedDeliverDate[productId] as ExpectedDeliveryStoredResponse[];
        const filtered = cachedArray?.filter(storedPincode => storedPincode.pincode === userPincode);
        expectedDeliveryCacheHit = filtered?.[0] || null;
        /* If estimated delivery response cached making sure cached date and present date are same */
        if (expectedDeliveryCacheHit) {
          expectedDeliveryCacheHit = new Date(expectedDeliveryCacheHit.createdAt).toLocaleDateString() === new Date().toLocaleDateString() ? expectedDeliveryCacheHit : null;
        }
      } catch (error) {
        expectedDeliveryCacheHit = null;
      }
      /* If response is cached returning cached address */
      if (expectedDeliveryCacheHit) {
        return of(expectedDeliveryCacheHit.pincodeResponse);
      }

      /* If response is not cached making api call and cache the response */
      return this.productsApiService.getExpectedDeliveryDates(productId, pincode)
        .pipe(
          tap((response: ExpectedDeliveryDates) => {
            this.expectedDeliveryDatesCache[productId] = [{ pincode: userPincode, createdAt: new Date(), pincodeResponse: response }];
            if (this.platformService.isBrowser()) {
              this.storageService.save("estimatedDeliveryDate", this.expectedDeliveryDatesCache);
            }
          })
        )
    } else {
      /* If user is not logged in response is cached in dictionary */
      try {
        const cachedArray = this.expectedDeliveryDatesCache[productId] as ExpectedDeliveryStoredResponse[];
        const filtered = cachedArray?.filter(storedExpectedDelivery => storedExpectedDelivery.pincode === userPincode);
        expectedDeliveryCacheHit = filtered?.[0] || null;
        if (expectedDeliveryCacheHit) {
          expectedDeliveryCacheHit = new Date(expectedDeliveryCacheHit.createdAt).toLocaleDateString() === new Date().toLocaleDateString() ? expectedDeliveryCacheHit : null;
        }
      } catch (error) {
        expectedDeliveryCacheHit = null;
      }
      if (expectedDeliveryCacheHit) {
        return of(expectedDeliveryCacheHit.pincodeResponse);
      }

      return this.productsApiService.getExpectedDeliveryDates(productId, pincode)
        .pipe(
          tap((response: ExpectedDeliveryDates) => {
            const existingCache = this.expectedDeliveryDatesCache[productId] || [];
            this.expectedDeliveryDatesCache[productId] = [...existingCache, { pincode: userPincode, createdAt: new Date(), pincodeResponse: response }];
          })
        )
    }

  }


  getExpectedDeliveryDatesCartPage(productId: string, pincode: string): Observable<ExpectedDeliveryDates> {

    let cachedEstimatedDeliverDate = null;
    if (this.platformService.isBrowser()) {
      try {
        cachedEstimatedDeliverDate = this.storageService.retrieve("estimatedDeliveryDate");
      } catch (error) {
        cachedEstimatedDeliverDate = null;
      }
    }
    const userPincode = pincode ? pincode : DEFAULT_DESTINATION_PINCODE;
    let expectedDeliveryCacheHit: ExpectedDeliveryStoredResponse | null = null;

    /* Checking estimated delivery response cached for given product id*/
    try {
      const cachedArray = cachedEstimatedDeliverDate[productId] as ExpectedDeliveryStoredResponse[];
      expectedDeliveryCacheHit = cachedArray?.[0] || null;
      if (expectedDeliveryCacheHit) {
        expectedDeliveryCacheHit = new Date(expectedDeliveryCacheHit.createdAt).toLocaleDateString() === new Date().toLocaleDateString() ? expectedDeliveryCacheHit : null;
      }
    } catch (error) {
      expectedDeliveryCacheHit = null;
    }
    /* If response is not cached making api call and cache the response */
    if (expectedDeliveryCacheHit) {
      return of(expectedDeliveryCacheHit.pincodeResponse);
    }

    return this.productsApiService.getExpectedDeliveryDates(productId, pincode)
      .pipe(
        tap((response: ExpectedDeliveryDates) => {
          this.expectedDeliveryDatesCache[productId] = [{ pincode: userPincode, createdAt: new Date(), pincodeResponse: response }];
          if (this.platformService.isBrowser()) {
            this.storageService.save("estimatedDeliveryDate", this.expectedDeliveryDatesCache);
          }
        })
      )
  }
}